import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  ArrowRight, Bot, Building2, Check, Clock, Lock, Mail, MapPin, Phone, Plus, Sparkles, User, Wallet, X,
} from 'lucide-react';
import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { EnhancePromptButton } from '@/components/EnhancePromptButton';
import { IndustryPicker } from '@/components/IndustryPicker';
import { LoadErrorState } from '@/components/ListStates';
import { SettingsCard, SettingsPage, SettingsRow, SettingsSaveBar, SettingsSection } from '@/components/settings/SettingsPage';
import {
  businessProfileService,
  AiEnhancedProfile,
  BusinessProfile,
  RequiredLeadField,
  UpsertBusinessProfilePayload,
} from '@/services/business-profile.service';
import { settingsService, type WorkspaceInfo } from '@/services/settings.service';
import { industryIcon, industryOption } from '@/lib/industries';
import {
  customObjectsService,
  CustomObjectType,
  TemplateListResponse,
} from '@/services/custom-objects.service';
import { useModuleAccess } from '@/contexts/ModuleAccessContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';

// Common WhatsApp-market languages. ISO 639-1 codes — short, predictable,
// what the AI fallback uses for per-language provider routing. Grouped by
// likelihood of use so the dropdown isn't a wall of dead options.
const LANGUAGE_OPTIONS: ReadonlyArray<{ code: string; label: string }> = [
  // Common in MENA / South Asia / international SMB markets
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'العربية (Arabic)' },
  { code: 'ur', label: 'اُردُو (Urdu)' },
  { code: 'hi', label: 'हिन्दी (Hindi)' },
  { code: 'fa', label: 'فارسی (Persian / Farsi)' },
  { code: 'tr', label: 'Türkçe (Turkish)' },
  // European
  { code: 'fr', label: 'Français (French)' },
  { code: 'es', label: 'Español (Spanish)' },
  { code: 'de', label: 'Deutsch (German)' },
  { code: 'it', label: 'Italiano (Italian)' },
  { code: 'pt', label: 'Português (Portuguese)' },
  { code: 'nl', label: 'Nederlands (Dutch)' },
  { code: 'pl', label: 'Polski (Polish)' },
  { code: 'sv', label: 'Svenska (Swedish)' },
  { code: 'ru', label: 'Русский (Russian)' },
  // Asian
  { code: 'zh', label: '中文 (Chinese, Simplified)' },
  { code: 'ja', label: '日本語 (Japanese)' },
  { code: 'ko', label: '한국어 (Korean)' },
  { code: 'vi', label: 'Tiếng Việt (Vietnamese)' },
  { code: 'id', label: 'Bahasa Indonesia' },
  { code: 'ms', label: 'Bahasa Melayu (Malay)' },
  { code: 'th', label: 'ไทย (Thai)' },
  // South Asian (additional)
  { code: 'bn', label: 'বাংলা (Bengali)' },
  { code: 'ta', label: 'தமிழ் (Tamil)' },
  { code: 'te', label: 'తెలుగు (Telugu)' },
  { code: 'ml', label: 'മലയാളം (Malayalam)' },
  { code: 'pa', label: 'ਪੰਜਾਬੀ (Punjabi)' },
  { code: 'mr', label: 'मराठी (Marathi)' },
  { code: 'gu', label: 'ગુજરાતી (Gujarati)' },
  { code: 'kn', label: 'ಕನ್ನಡ (Kannada)' },
  // African
  { code: 'sw', label: 'Kiswahili (Swahili)' },
  { code: 'am', label: 'አማርኛ (Amharic)' },
  { code: 'ha', label: 'Hausa' },
  { code: 'yo', label: 'Yorùbá (Yoruba)' },
];

function languageLabel(code: string): string {
  return LANGUAGE_OPTIONS.find((l) => l.code === code)?.label ?? code;
}

// ─── Operating hours picker ───────────────────────────────────────────────
// The DB stores `operatingHours` as a free-text string for backward compat
// with anything that already wrote a sentence-style value. We render a
// structured per-day picker as the editing surface and serialise it back to
// a readable, AI-friendly string on save:
//   "Monday 09:00–18:00; Tuesday 09:00–18:00; ... ; Sunday Closed"
// The string round-trips through `parseHoursString` so legacy free-text
// values can be shown to the operator (with a notice) but our picker is the
// authoritative editor going forward.

type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
type DayStatus = 'closed' | 'open24' | 'custom';

interface DayRow {
  status: DayStatus;
  open: string;   // "HH:MM" 24h
  close: string;  // "HH:MM" 24h
}

type HoursState = Record<DayKey, DayRow>;

const DAY_ORDER: ReadonlyArray<{ key: DayKey; label: string; short: string }> = [
  { key: 'mon', label: 'Monday',    short: 'Mon' },
  { key: 'tue', label: 'Tuesday',   short: 'Tue' },
  { key: 'wed', label: 'Wednesday', short: 'Wed' },
  { key: 'thu', label: 'Thursday',  short: 'Thu' },
  { key: 'fri', label: 'Friday',    short: 'Fri' },
  { key: 'sat', label: 'Saturday',  short: 'Sat' },
  { key: 'sun', label: 'Sunday',    short: 'Sun' },
];

const DEFAULT_HOURS: HoursState = {
  mon: { status: 'custom', open: '09:00', close: '18:00' },
  tue: { status: 'custom', open: '09:00', close: '18:00' },
  wed: { status: 'custom', open: '09:00', close: '18:00' },
  thu: { status: 'custom', open: '09:00', close: '18:00' },
  fri: { status: 'custom', open: '09:00', close: '18:00' },
  sat: { status: 'closed', open: '09:00', close: '18:00' },
  sun: { status: 'closed', open: '09:00', close: '18:00' },
};

const dayNameMap: Record<string, DayKey> = {
  monday: 'mon', mon: 'mon',
  tuesday: 'tue', tue: 'tue', tues: 'tue',
  wednesday: 'wed', wed: 'wed',
  thursday: 'thu', thu: 'thu', thur: 'thu', thurs: 'thu',
  friday: 'fri', fri: 'fri',
  saturday: 'sat', sat: 'sat',
  sunday: 'sun', sun: 'sun',
};

function parseHoursString(value: string): { hours: HoursState; matchedAll: boolean } {
  if (!value || !value.trim()) return { hours: DEFAULT_HOURS, matchedAll: false };

  const next: HoursState = JSON.parse(JSON.stringify(DEFAULT_HOURS));
  // Reset everything to closed so we can mark explicit matches.
  (Object.keys(next) as DayKey[]).forEach((k) => { next[k] = { ...next[k], status: 'closed' }; });

  let touched = 0;
  for (const chunk of value.split(/[;,\n]+/)) {
    const m = chunk.trim().match(/^([A-Za-z]+)\s+(.+)$/);
    if (!m) continue;
    const dayKey = dayNameMap[m[1].toLowerCase()];
    if (!dayKey) continue;
    const rest = m[2].trim().toLowerCase();
    if (rest === 'closed') {
      next[dayKey] = { status: 'closed', open: '09:00', close: '18:00' };
      touched++;
    } else if (rest === '24h' || rest === 'open 24h' || rest === '24 hours' || rest === 'all day') {
      next[dayKey] = { status: 'open24', open: '00:00', close: '23:59' };
      touched++;
    } else {
      const tm = rest.match(/^(\d{1,2}):?(\d{0,2})\s*[–-]\s*(\d{1,2}):?(\d{0,2})$/);
      if (tm) {
        const oh = tm[1].padStart(2, '0');
        const om = (tm[2] || '00').padStart(2, '0');
        const ch = tm[3].padStart(2, '0');
        const cm = (tm[4] || '00').padStart(2, '0');
        next[dayKey] = { status: 'custom', open: `${oh}:${om}`, close: `${ch}:${cm}` };
        touched++;
      }
    }
  }

  return { hours: touched > 0 ? next : DEFAULT_HOURS, matchedAll: touched === 7 };
}

function serializeHours(hours: HoursState): string {
  return DAY_ORDER.map(({ key, label }) => {
    const r = hours[key];
    if (r.status === 'closed') return `${label} Closed`;
    if (r.status === 'open24') return `${label} 24h`;
    return `${label} ${r.open}–${r.close}`;
  }).join('; ');
}

// The picker pushes its own serialisation up as soon as it mounts, so a stored
// value that is empty or free text reads back as the default schedule. Compare
// hours in this form, or the form would look edited right after it loads.
const normalizeHours = (value: string) => serializeHours(parseHoursString(value).hours);

// ─── Shared styling ───────────────────────────────────────────────────────────
// Same look as <Input> for the native controls the shared Select / Textarea
// don't match (rounded-lg, 4px focus ring).
const CONTROL_BASE =
  'rounded-lg border border-border bg-surface text-text-primary placeholder:text-text-muted transition-colors duration-150 hover:border-border-strong focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary-soft disabled:cursor-not-allowed disabled:opacity-60';
const SELECT_CLS = `${CONTROL_BASE} h-10 w-full px-3 text-sm`;
const TEXTAREA_CLS = `${CONTROL_BASE} w-full px-3.5 py-2.5 text-sm leading-relaxed`;
const COMPACT_CLS = `${CONTROL_BASE} h-9 px-2.5 text-xs`;

/** FormField ends with its own bottom margin; trimming the last one keeps the card padding even. */
function Fields({ children }: { children: ReactNode }) {
  return <div className="[&>:last-child]:mb-0">{children}</div>;
}

function Chip({ children, onRemove, removeLabel }: { children: ReactNode; onRemove: () => void; removeLabel: string }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1 rounded-full border border-primary/20 bg-primary-soft py-1 pl-2.5 pr-1.5 text-xs font-medium text-primary">
      <span className="min-w-0 break-words">{children}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={removeLabel}
        title="Remove"
        className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-primary/60 transition-colors hover:bg-danger-soft hover:text-danger"
      >
        <X size={10} />
      </button>
    </span>
  );
}

interface HoursPickerProps {
  value: string;
  onChange: (next: string) => void;
}

function HoursPicker({ value, onChange }: HoursPickerProps) {
  const initial = parseHoursString(value);
  const [hours, setHours] = useState<HoursState>(initial.hours);
  const [legacyText] = useState<string>(initial.matchedAll ? '' : (value || '').trim());

  // Whenever local state changes, serialize and push up so save uses the structured value.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { onChange(serializeHours(hours)); }, [hours]);

  const updateDay = (k: DayKey, patch: Partial<DayRow>) =>
    setHours((prev) => ({ ...prev, [k]: { ...prev[k], ...patch } }));

  const copyToAllDays = (src: DayKey) =>
    setHours((prev) => {
      const ref = prev[src];
      const next: HoursState = { ...prev };
      DAY_ORDER.forEach(({ key }) => { next[key] = { ...ref }; });
      return next;
    });

  const applyWeekdayWeekend = () =>
    setHours({
      mon: { status: 'custom', open: '09:00', close: '18:00' },
      tue: { status: 'custom', open: '09:00', close: '18:00' },
      wed: { status: 'custom', open: '09:00', close: '18:00' },
      thu: { status: 'custom', open: '09:00', close: '18:00' },
      fri: { status: 'custom', open: '09:00', close: '18:00' },
      sat: { status: 'closed', open: '09:00', close: '18:00' },
      sun: { status: 'closed', open: '09:00', close: '18:00' },
    });

  const apply247 = () =>
    setHours((prev) => {
      const next: HoursState = { ...prev };
      DAY_ORDER.forEach(({ key }) => { next[key] = { status: 'open24', open: '00:00', close: '23:59' }; });
      return next;
    });

  const applyAllClosed = () =>
    setHours((prev) => {
      const next: HoursState = { ...prev };
      DAY_ORDER.forEach(({ key }) => { next[key] = { ...prev[key], status: 'closed' }; });
      return next;
    });

  return (
    <div className="space-y-3">
      {legacyText && (
        <div className="rounded-xl border border-warning/30 bg-warning-soft px-3.5 py-2.5 text-xs leading-relaxed text-text-secondary">
          This text couldn't be read as a weekly schedule: <em className="break-words">“{legacyText}”</em>. The schedule below replaces it when you save.
        </div>
      )}

      <div className="flex flex-wrap gap-1.5">
        {[
          { label: 'Mon–Fri 9–6', apply: applyWeekdayWeekend },
          { label: 'Open 24/7', apply: apply247 },
          { label: 'All closed', apply: applyAllClosed },
        ].map((preset) => (
          <button
            key={preset.label}
            type="button"
            onClick={preset.apply}
            className="rounded-full border border-border bg-surface-alt px-2.5 py-1 text-xs text-text-secondary transition-colors hover:border-primary/50 hover:text-primary"
          >
            {preset.label}
          </button>
        ))}
      </div>

      <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-surface">
        {DAY_ORDER.map(({ key, label, short }) => {
          const row = hours[key];
          return (
            // Narrow: day + status + "Copy to all" on one line, the times on the next.
            // Wide: one line, with "Copy to all" pushed to the end (order-last).
            <li key={key} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3.5 py-2.5">
              <span className="w-10 shrink-0 text-sm font-semibold text-text-primary @lg:w-24">
                <span className="@lg:hidden">{short}</span>
                <span className="hidden @lg:inline">{label}</span>
              </span>
              <select
                aria-label={`${label} hours`}
                value={row.status}
                onChange={(e) => updateDay(key, { status: e.target.value as DayStatus })}
                className={clsx(COMPACT_CLS, 'min-w-0 flex-1 @lg:w-40 @lg:flex-none')}
              >
                <option value="custom">Open (set times)</option>
                <option value="open24">Open 24 hours</option>
                <option value="closed">Closed</option>
              </select>
              <button
                type="button"
                onClick={() => copyToAllDays(key)}
                title={`Copy ${label}'s schedule to all 7 days`}
                className="shrink-0 rounded-md px-2 py-1 text-2xs text-text-muted transition-colors hover:bg-surface-alt hover:text-primary @lg:order-last @lg:ml-auto"
              >
                Copy to all
              </button>
              {row.status === 'custom' ? (
                <div className="flex basis-full items-center gap-1.5 @lg:basis-auto">
                  <input
                    type="time"
                    aria-label={`${label} opens at`}
                    value={row.open}
                    onChange={(e) => updateDay(key, { open: e.target.value })}
                    className={clsx(COMPACT_CLS, 'min-w-0 flex-1 @lg:w-28 @lg:flex-none')}
                  />
                  <span className="text-2xs text-text-muted">to</span>
                  <input
                    type="time"
                    aria-label={`${label} closes at`}
                    value={row.close}
                    onChange={(e) => updateDay(key, { close: e.target.value })}
                    className={clsx(COMPACT_CLS, 'min-w-0 flex-1 @lg:w-28 @lg:flex-none')}
                  />
                </div>
              ) : (
                // The select already says it on narrow widths.
                <span className="hidden text-xs italic text-text-muted @lg:inline">
                  {row.status === 'open24' ? 'Open all day' : 'Closed for inquiries'}
                </span>
              )}
            </li>
          );
        })}
      </ul>

      <p className="px-1 text-2xs leading-relaxed text-text-muted">
        The AI reads this as: <span className="break-words font-mono">{serializeHours(hours)}</span>
      </p>
    </div>
  );
}

// ─── Form model ───────────────────────────────────────────────────────────────

type Tone = BusinessProfile['tone'];

const TONES: ReadonlyArray<{ value: Tone; title: string; text: string }> = [
  { value: 'formal', title: 'Formal', text: 'Professional and crisp.' },
  { value: 'friendly', title: 'Friendly', text: 'Warm and helpful.' },
  { value: 'casual', title: 'Casual', text: 'Relaxed and conversational.' },
];

const LEAD_PRESETS = [
  { key: 'name', label: 'Name', type: 'text', icon: User },
  { key: 'email', label: 'Email', type: 'email', icon: Mail },
  { key: 'phone', label: 'Phone', type: 'phone', icon: Phone },
  { key: 'budget', label: 'Budget', type: 'number', icon: Wallet },
  { key: 'company', label: 'Company', type: 'text', icon: Building2 },
  { key: 'city', label: 'City', type: 'text', icon: MapPin },
] as const;

const SLA_MAX = 10080;

/** Every field the Save button sends, so "unsaved changes" is just form vs. last saved copy. */
interface FormValues {
  industry: string;
  industryOther: string;
  oneLiner: string;
  productsServices: string;
  targetAudience: string;
  uniqueValueProp: string;
  tone: Tone;
  primaryLanguage: string;
  supportedLanguages: string[];
  operatingHours: string;
  responseSlaMinutes: number;
  requiredLeadFields: RequiredLeadField[];
  commonObjections: string[];
  additionalContext: string;
}

function valuesFromProfile(p: BusinessProfile | null, assignedIndustry: string | null): FormValues {
  return {
    industry: assignedIndustry ?? p?.industry ?? '',
    industryOther: p?.industryOther ?? '',
    oneLiner: p?.oneLiner ?? '',
    productsServices: p?.productsServices ?? '',
    targetAudience: p?.targetAudience ?? '',
    uniqueValueProp: p?.uniqueValueProp ?? '',
    tone: p?.tone ?? 'friendly',
    primaryLanguage: p?.primaryLanguage ?? 'en',
    supportedLanguages: p?.supportedLanguages ?? [],
    operatingHours: p?.operatingHours ?? '',
    responseSlaMinutes: p?.responseSlaMinutes ?? 60,
    requiredLeadFields: p?.requiredLeadFields ?? [],
    commonObjections: p?.commonObjections ?? [],
    additionalContext: p?.additionalContext ?? '',
  };
}

const formKey = (v: FormValues) => JSON.stringify({ ...v, operatingHours: normalizeHours(v.operatingHours) });

/** 90 → "1 h 30 min". Empty under an hour, where the number alone is clear. */
function humanMinutes(total: number): string {
  if (!Number.isInteger(total) || total < 60) return '';
  const d = Math.floor(total / 1440);
  const h = Math.floor((total % 1440) / 60);
  const m = total % 60;
  return [d && `${d} d`, h && `${h} h`, m && `${m} min`].filter(Boolean).join(' ');
}

// ─── Component ────────────────────────────────────────────────────────────────

const flagOf = (code?: string | null) =>
  code && /^[A-Za-z]{2}$/.test(code) ? String.fromCodePoint(...code.toUpperCase().split('').map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)) : null;

function countryName(code: string): string {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

/** The business name, country, currency and timezone belong to the company record (set by Slightor), so they're shown read-only. */
function BusinessIdentity({ info }: { info: WorkspaceInfo }) {
  const IndustryIcon = industryIcon(info.industry);
  const industry = industryOption(info.industry);
  const initials = info.companyName.split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  const flag = flagOf(info.country);

  return (
    <SettingsSection
      title="Your business"
      description="The business this workspace belongs to."
      aside={(
        <div className="space-y-2">
          <p className="flex items-start gap-1.5 text-2xs leading-relaxed text-text-muted">
            <Lock size={11} className="mt-0.5 shrink-0" />
            Set by Slightor for your account. Contact support to change them.
          </p>
          <Link to="/settings/plan" className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
            Plan & usage <ArrowRight size={12} />
          </Link>
        </div>
      )}
    >
      <SettingsCard padded={false}>
        <div className="flex items-center gap-4 p-5">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gold-gradient-soft font-display text-lg font-bold text-primary shadow-soft">
            {initials || <Building2 size={22} />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">Business name</div>
            <h2 className="truncate font-display text-lg font-bold tracking-tight text-text-primary">{info.companyName}</h2>
            <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-text-secondary">
              <IndustryIcon size={13} className="shrink-0" />
              <span className="truncate">
                {industry?.label ?? 'Industry not set yet'}
                {industry?.group && <span className="text-text-muted"> · {industry.group}</span>}
              </span>
            </div>
          </div>
        </div>
        <div className="divide-y divide-border border-t border-border">
          {info.country && (
            <SettingsRow
              icon={<MapPin size={16} />}
              label="Country"
              value={<span className="truncate">{flag ? `${flag} ` : ''}{countryName(info.country)}</span>}
              locked
            />
          )}
          <SettingsRow icon={<Wallet size={16} />} label="Currency" value={info.currency} locked />
          <SettingsRow icon={<Clock size={16} />} label="Timezone" value={info.timezone} locked />
        </div>
      </SettingsCard>
    </SettingsSection>
  );
}

export default function BusinessProfileSettings() {
  const navigate = useNavigate();
  const { info: workspace } = useWorkspace();
  const [profile, setProfile] = useState<BusinessProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Template install suggestion state
  const [installedTypes, setInstalledTypes] = useState<CustomObjectType[]>([]);
  const [templateData, setTemplateData] = useState<TemplateListResponse | null>(null);
  const [installingTemplateId, setInstallingTemplateId] = useState<string | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Form state
  const [industry, setIndustry] = useState('');
  // The platform assigned this company's industry (super-admin) — never editable here.
  const [industryAssigned, setIndustryAssigned] = useState(false);
  const [industryOther, setIndustryOther] = useState('');
  const [oneLiner, setOneLiner] = useState('');
  const [productsServices, setProductsServices] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [uniqueValueProp, setUniqueValueProp] = useState('');
  const [tone, setTone] = useState<'formal' | 'friendly' | 'casual'>('friendly');
  const [primaryLanguage, setPrimaryLanguage] = useState('en');
  const [supportedLanguages, setSupportedLanguages] = useState<string[]>([]);
  const [operatingHours, setOperatingHours] = useState('');
  const [responseSlaMinutes, setResponseSlaMinutes] = useState(60);
  const [requiredLeadFields, setRequiredLeadFields] = useState<RequiredLeadField[]>([]);
  const [commonObjections, setCommonObjections] = useState<string[]>([]);
  const [objectionInput, setObjectionInput] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [enhanceOpen, setEnhanceOpen] = useState(false);
  const [enhanceDescription, setEnhanceDescription] = useState('');
  const [enhancing, setEnhancing] = useState(false);

  // The form as last loaded or saved — "unsaved changes" means the form differs from this.
  const [saved, setSaved] = useState<FormValues | null>(null);
  // HoursPicker only reads `value` when it mounts, so a value set from outside
  // (AI draft, Discard) has to remount it to show up.
  const [hoursKey, setHoursKey] = useState(0);

  const values: FormValues = {
    industry, industryOther, oneLiner, productsServices, targetAudience, uniqueValueProp,
    tone, primaryLanguage, supportedLanguages, operatingHours, responseSlaMinutes,
    requiredLeadFields, commonObjections, additionalContext,
  };

  const applyValues = (v: FormValues) => {
    setIndustry(v.industry);
    setIndustryOther(v.industryOther);
    setOneLiner(v.oneLiner);
    setProductsServices(v.productsServices);
    setTargetAudience(v.targetAudience);
    setUniqueValueProp(v.uniqueValueProp);
    setTone(v.tone);
    setPrimaryLanguage(v.primaryLanguage);
    setSupportedLanguages(v.supportedLanguages);
    setOperatingHours(v.operatingHours);
    setResponseSlaMinutes(v.responseSlaMinutes);
    setRequiredLeadFields(v.requiredLeadFields);
    setCommonObjections(v.commonObjections);
    setAdditionalContext(v.additionalContext);
  };

  // Apply an AI-generated draft to ALL fields. Operator can still edit before save.
  // We never touch `industry` here — that's locked to the operator's onboarding choice.
  const applyEnhancedProfile = (draft: AiEnhancedProfile) => {
    if (draft.oneLiner !== undefined) setOneLiner(draft.oneLiner);
    if (draft.productsServices !== undefined) setProductsServices(draft.productsServices);
    if (draft.targetAudience !== undefined) setTargetAudience(draft.targetAudience);
    if (draft.uniqueValueProp !== undefined) setUniqueValueProp(draft.uniqueValueProp);
    if (draft.tone) setTone(draft.tone);
    if (draft.operatingHours !== undefined) { setOperatingHours(draft.operatingHours); setHoursKey((k) => k + 1); }
    if (typeof draft.responseSlaMinutes === 'number') setResponseSlaMinutes(draft.responseSlaMinutes);
    if (Array.isArray(draft.commonObjections)) setCommonObjections(draft.commonObjections);
    if (draft.additionalContext !== undefined) setAdditionalContext(draft.additionalContext);
    if (Array.isArray(draft.requiredLeadFields)) setRequiredLeadFields(draft.requiredLeadFields);
  };

  const handleAiEnhance = async () => {
    if (enhanceDescription.trim().length < 10) {
      toast.error('Add a bit more detail — at least one or two sentences about your business.');
      return;
    }
    setEnhancing(true);
    try {
      const draft = await businessProfileService.aiEnhance(enhanceDescription.trim());
      applyEnhancedProfile(draft);
      toast.success('Profile drafted. Review the fields below and click Save when you\'re happy.');
      setEnhanceOpen(false);
    } catch (e: unknown) {
      // The global interceptor already toasts API errors — only a network failure needs one here.
      const err = e as { response?: unknown };
      if (!err?.response) toast.error('AI enhance failed');
    } finally {
      setEnhancing(false);
    }
  };


  // Side-load installed types + matched templates so we can suggest installing
  // the right vertical when the user finishes their profile. Only with the
  // Custom objects module — without it both calls 403 and the global
  // interceptor toasted "module not enabled" on every visit.
  const { has, loading: modulesLoading } = useModuleAccess();
  const customObjectsOn = !modulesLoading && has('custom_objects');
  const refreshTemplateBanner = async () => {
    if (!customObjectsOn) { setInstalledTypes([]); setTemplateData(null); return; }
    try {
      const [types, tpls] = await Promise.all([
        customObjectsService.listTypes(false),
        customObjectsService.listTemplates(),
      ]);
      setInstalledTypes(types);
      setTemplateData(tpls);
    } catch { /* silent */ }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refreshTemplateBanner(); }, [customObjectsOn]);

  const onInstallTemplate = async (templateId: string) => {
    setInstallingTemplateId(templateId);
    try {
      const r = await customObjectsService.installTemplate(templateId);
      if (r.installed.length > 0) {
        toast.success(`Installed: ${r.installed.join(', ')}`);
      } else {
        toast(`Already installed`);
      }
      await refreshTemplateBanner();
    } catch (e: unknown) {
      if (!(e as { response?: unknown })?.response) toast.error('Install failed');
    } finally { setInstallingTemplateId(null); }
  };

  // Load. A 404 ("not set up yet") comes back from the service as null and
  // leaves the form empty; any other failure shows a retry instead of an empty
  // form that would look like the real profile.
  const load = () => {
    setLoading(true);
    setLoadFailed(false);
    Promise.all([businessProfileService.getByCompany(), settingsService.workspace().catch(() => null)])
      .then(([p, workspace]) => {
        const assigned = workspace?.industryLocked && workspace.industry ? workspace.industry : null;
        const next = valuesFromProfile(p, assigned);
        setProfile(p);
        setIndustryAssigned(assigned !== null);
        applyValues(next);
        setSaved(next);
      })
      .catch(() => setLoadFailed(true))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const slaInvalid = !Number.isInteger(responseSlaMinutes) || responseSlaMinutes < 1 || responseSlaMinutes > SLA_MAX;

  /** true when the profile was saved, so callers can chain on it. */
  const handleSave = async (): Promise<boolean> => {
    if (!industry) { toast.error('Choose your industry first'); return false; }
    if (slaInvalid) { toast.error('Response time must be a whole number of minutes, from 1 to 10,080'); return false; }
    setSaving(true);
    try {
      const payload: UpsertBusinessProfilePayload = {
        industry: industry as BusinessProfile['industry'],
        industryOther: industry === 'other' ? industryOther : undefined,
        oneLiner, productsServices, targetAudience, uniqueValueProp,
        tone, primaryLanguage, supportedLanguages,
        operatingHours, responseSlaMinutes,
        requiredLeadFields, commonObjections, additionalContext,
      };
      const updated = await businessProfileService.upsert(payload);
      setProfile(updated);
      setSaved(values);
      toast.success('Business profile saved');
      // Re-evaluate the suggestion banner with the new industry value.
      refreshTemplateBanner();
      return true;
    } catch (e: unknown) {
      if (!(e as { response?: unknown })?.response) toast.error('Failed to save business profile');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    if (!saved) return;
    applyValues(saved);
    setHoursKey((k) => k + 1);
  };

  const generateFlow = async () => {
    setGenerating(true);
    try {
      if (await handleSave()) navigate('/leads/bot-flows', { state: { autoGenerate: true } });
    } finally {
      setGenerating(false);
    }
  };

  const addLeadField = () => {
    setRequiredLeadFields(prev => [
      ...prev,
      { key: `field_${Date.now()}`, label: '', type: 'text', required: true },
    ]);
  };

  const updateLeadField = (idx: number, patch: Partial<RequiredLeadField>) => {
    setRequiredLeadFields(prev => prev.map((f, i) => i === idx ? { ...f, ...patch } : f));
  };

  const removeLeadField = (idx: number) => {
    setRequiredLeadFields(prev => prev.filter((_, i) => i !== idx));
  };

  const addObjection = () => {
    const trimmed = objectionInput.trim();
    if (trimmed && !commonObjections.includes(trimmed)) {
      setCommonObjections(prev => [...prev, trimmed]);
      setObjectionInput('');
    }
  };

  const industryReadOnly = industryAssigned || profile !== null;
  const IndustryIcon = industryIcon(industry);

  const header = {
    eyebrow: 'Workspace',
    title: 'Business profile',
    description: 'Tell the AI what you sell, who your customers are and how you want to sound. The WhatsApp bot, the flow builder and the copilot read this.',
  };

  if (loading || loadFailed) {
    return (
      <SettingsPage {...header}>
        {loadFailed ? (
          <div className="py-7">
            <SettingsCard padded={false}>
              <LoadErrorState bare what="your business profile" onRetry={load} />
            </SettingsCard>
          </div>
        ) : (
          <div className="space-y-4 py-7">
            <div className="skeleton h-44 rounded-2xl" />
            <div className="skeleton h-56 rounded-2xl" />
            <div className="skeleton h-44 rounded-2xl" />
          </div>
        )}
      </SettingsPage>
    );
  }

  // Banner appears when the user has saved an industry but hasn't installed any
  // recommended templates yet. We hide it once any installed type matches a
  // template from the recommended list.
  const recommendedTemplates = templateData?.recommended ?? [];
  const installedTemplateIds = new Set(
    installedTypes.map((t) => t.installedFromTemplate).filter(Boolean) as string[],
  );
  const uninstalledRecommended = recommendedTemplates.filter((t) => !installedTemplateIds.has(t.id));
  const showTemplateBanner =
    !bannerDismissed &&
    profile !== null && // already saved at least once
    industry !== 'other' &&
    uninstalledRecommended.length > 0;

  const changed = saved !== null && formKey(values) !== formKey(saved);
  // A profile that was never saved has nothing to compare against, but still needs saving.
  const unsaved = profile === null || changed;
  const industryInfo = industryOption(industry);
  const slaReadout = humanMinutes(responseSlaMinutes);
  const templateTypeNames = uninstalledRecommended.flatMap((t) => t.types.map((ty) => ty.pluralLabel));

  return (
    <>
      <SettingsPage
        {...header}
        actions={(
          <Button
            variant="secondary"
            iconLeft={<Sparkles size={14} />}
            onClick={() => setEnhanceOpen(true)}
            title="Describe your business in a few sentences and AI will fill out the profile for you"
          >
            Enhance with AI
          </Button>
        )}
      >
        {workspace && <BusinessIdentity info={workspace} />}

        {showTemplateBanner && (
          <div className="py-6">
            <div className="rounded-2xl border border-primary/30 bg-surface bg-gold-gradient-soft p-5 shadow-soft">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary">
                  <Sparkles size={20} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-text-primary">Install a starter template for your business</div>
                      <p className="mt-0.5 text-xs leading-relaxed text-text-muted">
                        We matched {uninstalledRecommended.length} template{uninstalledRecommended.length === 1 ? '' : 's'} to your industry.
                        {' '}It adds data types ({templateTypeNames.slice(0, 6).join(', ')}{templateTypeNames.length > 6 ? '…' : ''}) so the AI can answer questions about your business right away.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setBannerDismissed(true)}
                      aria-label="Dismiss"
                      title="Dismiss"
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {uninstalledRecommended.map((tpl) => (
                      <Button
                        key={tpl.id}
                        size="sm"
                        variant="secondary"
                        loading={installingTemplateId === tpl.id}
                        onClick={() => onInstallTemplate(tpl.id)}
                      >
                        Install {tpl.label}
                      </Button>
                    ))}
                    <Button size="sm" variant="ghost" onClick={() => navigate('/custom-objects')}>
                      Browse all templates →
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <SettingsSection
          title="Business basics"
          description="Who you are. The AI opens with this when it introduces your business to new customers."
          aside={industryReadOnly ? (
            <p className="flex items-start gap-1.5 text-2xs leading-relaxed text-text-muted">
              <Lock size={11} className="mt-0.5 shrink-0" />
              {industryAssigned
                ? 'Your industry is set by your Slightor account manager. Contact support to change it.'
                : 'Your industry is locked once saved. Contact support if you need to switch.'}
            </p>
          ) : undefined}
        >
          {/* The industry picker's list opens downwards; the card's default overflow-hidden would clip it. */}
          <SettingsCard padded={false} className={industryReadOnly ? undefined : '!overflow-visible'}>
            {industryReadOnly ? (
              <SettingsRow
                icon={<IndustryIcon size={16} />}
                label="Industry"
                hint={industryInfo?.group}
                value={industryInfo?.label ?? (industry || '—')}
                locked
              />
            ) : (
              <div className="p-5">
                <Fields>
                  <FormField
                    label="Industry"
                    required
                    hintBelow
                    hint="Drives the AI bot's guardrails and the default services, departments and lead fields. You can't change it after you save."
                  >
                    <IndustryPicker value={industry} onChange={setIndustry} />
                  </FormField>
                </Fields>
              </div>
            )}

            {industry === 'other' && (
              <div className="border-t border-border p-5">
                <Fields>
                  <FormField
                    label="Describe your industry"
                    htmlFor="bp-industry-other"
                    hintBelow
                    hint="A short phrase for what you do. The AI uses it to set its tone and to turn down off-topic questions."
                  >
                    <Input
                      id="bp-industry-other"
                      value={industryOther}
                      onChange={(e) => setIndustryOther(e.target.value)}
                      placeholder="e.g. Luxury car detailing"
                    />
                  </FormField>
                </Fields>
              </div>
            )}

            <div className="border-t border-border p-5">
              <Fields>
                <FormField
                  label="One-liner"
                  htmlFor="bp-one-liner"
                  hintBelow
                  hint="Your pitch in one sentence, up to 120 characters. The AI uses it as the opening line about your business."
                >
                  <Input
                    id="bp-one-liner"
                    maxLength={120}
                    value={oneLiner}
                    onChange={(e) => setOneLiner(e.target.value)}
                    placeholder="e.g. We deliver fresh sushi in Dubai within 30 minutes"
                  />
                </FormField>
              </Fields>
            </div>
          </SettingsCard>
        </SettingsSection>

        <SettingsSection
          title="What you sell"
          description={'A short summary of your products and services. The AI uses it when a customer asks "what do you do?".'}
        >
          <SettingsCard>
            <Fields>
              <FormField
                label="Products & services"
                htmlFor="bp-products"
                hintBelow
                hint="If you set a services list on a WhatsApp account, that list is used instead of this."
              >
                <textarea
                  id="bp-products"
                  rows={4}
                  className={`${TEXTAREA_CLS} resize-y`}
                  value={productsServices}
                  onChange={(e) => setProductsServices(e.target.value)}
                  placeholder="Describe your main offerings, e.g. web design, mobile apps, monthly retainer..."
                />
              </FormField>
            </Fields>
          </SettingsCard>
        </SettingsSection>

        <SettingsSection
          title="Audience & positioning"
          description={'Who you sell to and why they should pick you. The AI uses it to match its tone and to answer "why you?".'}
        >
          <SettingsCard>
            <Fields>
              <FormField
                label="Target audience"
                htmlFor="bp-audience"
                hintBelow
                hint="Who you usually sell to. Small business owners need different framing than big company buyers."
              >
                <Input
                  id="bp-audience"
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value)}
                  placeholder="e.g. SMBs in the UAE, young professionals 25–40"
                />
              </FormField>
              <FormField
                label="Unique value proposition"
                htmlFor="bp-uvp"
                hintBelow
                hint="What sets you apart from competitors. The AI brings it up when a customer is comparing options or hesitating."
              >
                <Input
                  id="bp-uvp"
                  value={uniqueValueProp}
                  onChange={(e) => setUniqueValueProp(e.target.value)}
                  placeholder="e.g. 24-hour delivery, cheapest prices, most experienced team"
                />
              </FormField>
            </Fields>
          </SettingsCard>
        </SettingsSection>

        <SettingsSection
          title="Tone & languages"
          description="How the AI bot sounds, and which languages it uses with your customers."
        >
          <SettingsCard>
            <Fields>
              <FormField label="Tone">
                <div role="radiogroup" aria-label="Tone" className="grid gap-2 @4xl:grid-cols-3">
                  {TONES.map((t) => {
                    const on = tone === t.value;
                    return (
                      <button
                        key={t.value}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        onClick={() => setTone(t.value)}
                        className={clsx(
                          'relative rounded-xl border p-3.5 text-left transition-colors',
                          on ? 'border-primary bg-primary-soft/60' : 'border-border hover:border-border-strong',
                        )}
                      >
                        {on && (
                          <span className="absolute right-2.5 top-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-white">
                            <Check size={12} />
                          </span>
                        )}
                        <span className="block pr-6 text-sm font-semibold text-text-primary">{t.title}</span>
                        <span className="mt-0.5 block text-xs text-text-muted">{t.text}</span>
                      </button>
                    );
                  })}
                </div>
              </FormField>
            </Fields>
          </SettingsCard>

          <SettingsCard>
            <Fields>
              <FormField
                label="Primary language"
                htmlFor="bp-primary-language"
                hintBelow
                hint="The language the AI replies in by default. If a customer switches language, the bot follows them."
              >
                <select
                  id="bp-primary-language"
                  className={SELECT_CLS}
                  value={primaryLanguage}
                  onChange={(e) => {
                    const next = e.target.value;
                    setPrimaryLanguage(next);
                    // If the picked primary was previously in supported, drop it from there to avoid duplication.
                    setSupportedLanguages(prev => prev.filter(x => x !== next));
                  }}
                >
                  {LANGUAGE_OPTIONS.map(l => (
                    <option key={l.code} value={l.code}>{l.label}</option>
                  ))}
                </select>
              </FormField>

              <FormField
                label="Also supported in"
                htmlFor="bp-extra-language"
                hintBelow
                hint="Other languages your team can handle. Pick one to add it. The bot answers in these when customers write in them."
              >
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {supportedLanguages.length === 0 && (
                    <span className="text-xs text-text-muted">No additional languages yet.</span>
                  )}
                  {supportedLanguages.map(l => (
                    <Chip
                      key={l}
                      removeLabel={`Remove ${languageLabel(l)}`}
                      onRemove={() => setSupportedLanguages(p => p.filter(x => x !== l))}
                    >
                      {languageLabel(l)}
                    </Chip>
                  ))}
                </div>
                <select
                  id="bp-extra-language"
                  className={SELECT_CLS}
                  value=""
                  onChange={(e) => {
                    const code = e.target.value;
                    if (!code) return;
                    if (code === primaryLanguage) return;
                    if (supportedLanguages.includes(code)) return;
                    setSupportedLanguages(prev => [...prev, code]);
                  }}
                >
                  <option value="">— Add a language —</option>
                  {LANGUAGE_OPTIONS
                    .filter((l) => l.code !== primaryLanguage && !supportedLanguages.includes(l.code))
                    .map((l) => (
                      <option key={l.code} value={l.code}>{l.label}</option>
                    ))
                  }
                </select>
              </FormField>
            </Fields>
          </SettingsCard>
        </SettingsSection>

        <SettingsSection
          title="Hours & response time"
          description={'When your team can follow up, and how fast you promise to reply once the AI hands a lead over. The AI uses both to set the right expectation, like "we\'ll call tomorrow morning" or "within the hour".'}
        >
          <SettingsCard>
            <div className="mb-3 text-[13px] font-medium text-text-secondary">Operating hours</div>
            <HoursPicker key={hoursKey} value={operatingHours} onChange={setOperatingHours} />
          </SettingsCard>

          <SettingsCard>
            <Fields>
              <FormField
                label="Response time"
                htmlFor="bp-sla"
                hintBelow
                hint={`How fast a teammate replies once the AI hands a lead over. The AI tells customers this.${slaReadout ? ` That is ${slaReadout}.` : ''}`}
                error={slaInvalid ? 'Enter a whole number of minutes, from 1 to 10,080 (7 days).' : undefined}
              >
                <div className="w-44">
                  <Input
                    id="bp-sla"
                    type="number"
                    min={1}
                    max={SLA_MAX}
                    value={responseSlaMinutes}
                    onChange={(e) => setResponseSlaMinutes(Number(e.target.value))}
                    invalid={slaInvalid}
                    iconRight={<span className="text-xs">min</span>}
                  />
                </div>
              </FormField>
            </Fields>
          </SettingsCard>
        </SettingsSection>

        <SettingsSection
          title="Lead capture fields"
          description="What the bot asks every new lead. Each WhatsApp account can override this list; this is the default for the whole company."
        >
          <SettingsCard
            footer={(
              <>
                <span className="mr-auto text-xs text-text-muted">
                  {requiredLeadFields.length === 0 ? 'No fields yet' : `${requiredLeadFields.length} field${requiredLeadFields.length === 1 ? '' : 's'}`}
                </span>
                <Button size="sm" variant="secondary" iconLeft={<Plus size={13} />} onClick={addLeadField}>
                  Add custom field
                </Button>
              </>
            )}
          >
            {/* Quick-add presets — the common fields most businesses want, one click each. */}
            <div className="mb-4">
              <div className="mb-2 text-[13px] font-medium text-text-secondary">Quick add</div>
              <div className="flex flex-wrap gap-1.5">
                {LEAD_PRESETS.map((preset) => {
                  const already = requiredLeadFields.some((f) => f.key === preset.key);
                  const Icon = already ? Check : preset.icon;
                  return (
                    <button
                      key={preset.key}
                      type="button"
                      disabled={already}
                      onClick={() => setRequiredLeadFields((prev) => [
                        ...prev,
                        { key: preset.key, label: preset.label, type: preset.type, required: true },
                      ])}
                      className={clsx(
                        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                        already
                          ? 'cursor-not-allowed border-border bg-surface-alt text-text-muted opacity-60'
                          : 'border-primary/30 bg-primary-soft text-primary hover:bg-primary/15',
                      )}
                      title={already ? 'Already added' : `Add ${preset.label} field`}
                    >
                      <Icon size={12} />
                      {preset.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {requiredLeadFields.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border px-4 py-8 text-center text-sm text-text-muted">
                No fields yet. Pick one above or add a custom field.
              </div>
            ) : (
              <>
                <ul className="space-y-2">
                  {requiredLeadFields.map((field, idx) => (
                    <li key={idx} className="rounded-xl border border-border bg-surface-alt/40 p-3">
                      <div className="flex flex-wrap items-start gap-x-2 gap-y-2">
                        <div className="min-w-0 flex-[2_1_14rem]">
                          <input
                            type="text"
                            aria-label="Field name"
                            placeholder="e.g. Budget, Project Type, Preferred Date"
                            className={`${CONTROL_BASE} h-10 w-full px-3 text-sm`}
                            value={field.label}
                            onChange={e =>
                              updateLeadField(idx, {
                                label: e.target.value,
                                key: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''),
                              })
                            }
                          />
                          {field.key && (
                            <div className="mt-1 break-all px-1 font-mono text-2xs text-text-muted">key: {field.key}</div>
                          )}
                        </div>
                        <div className="flex min-w-0 flex-[1_1_15rem] items-center gap-2">
                          <select
                            aria-label="Field type"
                            value={field.type}
                            onChange={e => updateLeadField(idx, { type: e.target.value as RequiredLeadField['type'] })}
                            className={clsx(SELECT_CLS, 'min-w-0 flex-1')}
                            title="What kind of value the bot expects (validates the answer before saving)."
                          >
                            <option value="text">Text</option>
                            <option value="phone">Phone</option>
                            <option value="email">Email</option>
                            <option value="number">Number</option>
                            <option value="choice">Choice (dropdown)</option>
                          </select>
                          <label className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 text-xs text-text-secondary">
                            <input
                              type="checkbox"
                              checked={field.required}
                              onChange={e => updateLeadField(idx, { required: e.target.checked })}
                              className="accent-primary"
                            />
                            Required
                          </label>
                          <button
                            type="button"
                            onClick={() => removeLeadField(idx)}
                            aria-label={`Remove ${field.label || 'this'} field`}
                            title="Remove this field"
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-danger-soft hover:text-danger"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-2xs leading-relaxed text-text-muted">
                  <strong className="font-semibold text-text-secondary">Required</strong> means the bot must capture this before it marks the lead as qualified.{' '}
                  <strong className="font-semibold text-text-secondary">Type</strong> tells the bot how to check the answer.
                </p>
              </>
            )}
          </SettingsCard>
        </SettingsSection>

        <SettingsSection
          title="Common objections"
          description={'The pushbacks you hear most, like "too expensive" or "not the right time". The AI acknowledges them calmly instead of getting stuck.'}
        >
          <SettingsCard>
            <div className="mb-3 flex flex-wrap gap-1.5">
              {commonObjections.length === 0 && (
                <span className="text-xs text-text-muted">No objections added yet.</span>
              )}
              {commonObjections.map((obj, i) => (
                <Chip
                  key={`${obj}-${i}`}
                  removeLabel={`Remove ${obj}`}
                  onRemove={() => setCommonObjections(p => p.filter(x => x !== obj))}
                >
                  {obj}
                </Chip>
              ))}
            </div>
            <div className="flex gap-2">
              <div className="min-w-0 flex-1">
                <Input
                  aria-label="Add an objection"
                  value={objectionInput}
                  onChange={e => setObjectionInput(e.target.value)}
                  placeholder={`"too expensive", "not the right time"...`}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); addObjection(); }
                  }}
                />
              </div>
              <Button variant="secondary" iconLeft={<Plus size={14} />} onClick={addObjection} disabled={!objectionInput.trim()}>Add</Button>
            </div>
          </SettingsCard>
        </SettingsSection>

        <SettingsSection
          title="Anything else the AI should know?"
          description="Notes that don't fit above: current offers, things to avoid saying, special routing rules, brand quirks."
        >
          <SettingsCard>
            <Fields>
              <FormField label="Notes for the AI" htmlFor="bp-context">
                <textarea
                  id="bp-context"
                  rows={5}
                  className={`${TEXTAREA_CLS} resize-y`}
                  value={additionalContext}
                  onChange={(e) => setAdditionalContext(e.target.value)}
                  placeholder="e.g. We're running a 30% discount in Ramadan. Don't mention competitor X. Leads from Abu Dhabi go to Ahmad."
                />
              </FormField>
            </Fields>
          </SettingsCard>
        </SettingsSection>

        <SettingsSection
          title="Bot flow"
          description="Turn this profile into a ready-made bot flow. Your changes are saved first."
        >
          <SettingsCard padded={false}>
            <div className="flex flex-wrap items-center gap-4 px-5 py-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary">
                <Bot size={20} />
              </span>
              <div className="min-w-0 flex-1 basis-56">
                <div className="text-sm font-semibold text-text-primary">Generate a bot flow from this profile</div>
                <div className="mt-0.5 text-xs text-text-muted">AI reads your business context and builds a flow that fits it.</div>
              </div>
              <Button size="sm" iconLeft={<Sparkles size={14} />} loading={generating} disabled={saving} onClick={generateFlow}>
                Generate flow
              </Button>
            </div>
          </SettingsCard>
        </SettingsSection>
      </SettingsPage>

      {unsaved && (
        <SettingsSaveBar
          message={changed ? 'Unsaved changes' : 'Not saved yet'}
          note={slaInvalid && <span className="text-xs font-normal text-danger">· Fix the response time to save</span>}
          onDiscard={changed ? discard : undefined}
          onSave={handleSave}
          saving={saving}
          disabled={slaInvalid}
          saveLabel={profile === null ? 'Save profile' : 'Save changes'}
        />
      )}

      {/* Rendered outside SettingsPage on purpose: that element is a size container, which makes
          fixed-position descendants position against it instead of the viewport. */}
      <Modal
        open={enhanceOpen}
        onClose={() => setEnhanceOpen(false)}
        title="Enhance profile with AI"
        widthClass="max-w-xl"
      >
        <div className="space-y-3">
          <div className="rounded-xl border border-primary/20 bg-primary-soft p-3 text-xs leading-relaxed text-text-secondary">
            <strong className="font-semibold text-text-primary">How this works:</strong> write a few sentences about your business: what you sell,
            who your customers are and anything special about how you work. AI drafts every field on this page.
            Your <strong className="font-semibold text-text-primary">industry</strong> stays as it is, and you can edit anything before you save.
          </div>

          <div>
            <div className="mb-1.5 flex items-center justify-between gap-3">
              <label htmlFor="bp-enhance" className="text-[13px] font-medium text-text-secondary">Describe your business</label>
              <EnhancePromptButton
                value={enhanceDescription}
                onChange={setEnhanceDescription}
                context="Business profile description for an AI to expand into a structured profile"
                disabled={enhancing}
                label="Polish my description"
              />
            </div>
            <textarea
              id="bp-enhance"
              rows={6}
              value={enhanceDescription}
              onChange={(e) => setEnhanceDescription(e.target.value.slice(0, 4000))}
              placeholder={
                "e.g. We're a Dubai-based flower shop. We do fresh bouquets, custom event arrangements, and corporate gifting. " +
                "Most customers are individuals shopping for birthdays / anniversaries, plus some hotels and offices. " +
                "Same-day delivery within Dubai. We offer Apple Pay and card. Closed on Fridays."
              }
              className={`${TEXTAREA_CLS} resize-none`}
            />
            <div className="mt-1 text-2xs text-text-muted">
              The more specific you are, the better the draft. Mention products, customers, pricing, what makes you different, and opening hours.
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setEnhanceOpen(false)} disabled={enhancing}>Cancel</Button>
            <Button
              variant="primary"
              iconLeft={<Sparkles size={14} />}
              loading={enhancing}
              disabled={enhancing || enhanceDescription.trim().length < 10}
              onClick={handleAiEnhance}
            >
              Enhance
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
